import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import ProjectVideoPlayer from './components/ProjectVideoPlayer';
import ProjectComments from './components/ProjectComments';
import ProjectSidebar from './components/ProjectSidebar';
import ProjectRatings from './components/ProjectRatings';
import { getAIProjectBySlug, getAIProjectComments, getRelatedAIProjects, getAIProjectReviews } from '../../../mocks/aiProjects';
import { extractAIProjectSlugFromParam, generateBreadcrumbs } from '../../../hooks/useNavigation';

const AIProjectDetailPage = () => {
  const { urlParam } = useParams<{ urlParam: string }>();
  const [activeTab, setActiveTab] = useState<'about' | 'behind-scenes' | 'reviews' | 'comments'>('about');

  const projectSlug = extractAIProjectSlugFromParam(urlParam);

  const project = useMemo(() => {
    return getAIProjectBySlug(projectSlug);
  }, [projectSlug]);

  const comments = useMemo(() => {
    if (!project) return [];
    return getAIProjectComments(project.id);
  }, [project]);

  const reviews = useMemo(() => {
    if (!project) return [];
    return getAIProjectReviews(project.id);
  }, [project]);

  const relatedProjects = useMemo(() => {
    if (!project) return [];
    return getRelatedAIProjects(project.id, 4);
  }, [project]);

  const breadcrumbs = useMemo(() => {
    if (!project) return [];
    return generateBreadcrumbs('aiProject', { title: project.title });
  }, [project]);

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-slate-800 rounded-full">
            <i className="ri-film-line text-4xl text-slate-500"></i>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Project Not Found</h1>
          <p className="text-slate-400 mb-8">The AI drama project you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link
            to="/dramas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-rose-600 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dramas
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />

      {/* Back Button + Breadcrumbs */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <Link
            to="/dramas?aidramasgallery"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dramas
          </Link>
          <nav className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center">
                {index > 0 && <i className="ri-arrow-right-s-line text-slate-600"></i>}
                {crumb.href === '#' ? (
                  <span className="text-white font-medium truncate max-w-xs">{crumb.label}</span>
                ) : (
                  <Link to={crumb.href} className="text-slate-400 hover:text-orange-400 transition-colors">
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <ProjectVideoPlayer project={project} />

            {/* Title & Meta */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{project.title}</h1>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-semibold rounded-full">
                      {project.category}
                    </span>
                    <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full">
                      {project.style}
                    </span>
                    {/* Rating Badge */}
                    {project.averageRating && project.averageRating > 0 && (
                      <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded-full">
                        <i className="ri-star-fill"></i>
                        {project.averageRating.toFixed(1)}
                        <span className="text-amber-400/70">({project.totalReviews})</span>
                      </span>
                    )}
                    {project.featured && (
                      <span className="px-3 py-1 bg-gradient-to-r from-orange-500/20 to-rose-500/20 text-orange-400 text-xs font-semibold rounded-full flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-400"></i>
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-700">
              <nav className="flex gap-1">
                {[
                  { id: 'about' as const, label: 'About', icon: 'ri-information-line' },
                  { id: 'behind-scenes' as const, label: 'Behind the Scenes', icon: 'ri-movie-line' },
                  { id: 'reviews' as const, label: `Reviews (${project.totalReviews || 0})`, icon: 'ri-star-line' },
                  { id: 'comments' as const, label: `Comments (${project.comments})`, icon: 'ri-chat-3-line' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-400'
                        : 'border-transparent text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'about' && (
              <div className="space-y-6">
                {/* Description */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h2 className="text-lg font-bold text-white mb-4">About This Project</h2>
                  <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                    {project.longDescription}
                  </div>
                </div>

                {/* Soundtrack */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                    <i className="ri-music-2-line text-orange-400"></i>
                    Soundtrack
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{project.soundtrack}</p>
                </div>
              </div>
            )}

            {activeTab === 'behind-scenes' && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-movie-line text-orange-400"></i>
                    Behind the Scenes
                  </h2>
                  <p className="text-slate-300 text-sm leading-relaxed">{project.behindTheScenes}</p>
                </div>

                {/* Tools Breakdown */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-tools-line text-orange-400"></i>
                    Tools &amp; Workflow
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {project.tools.map((tool, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg border border-slate-700/50"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-orange-500/20 to-rose-500/20 rounded-lg">
                          <i className="ri-cpu-line text-orange-400"></i>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-white">{tool}</span>
                          <p className="text-xs text-slate-500">
                            {index === 0 ? 'Primary video generation' :
                             index === 1 ? 'Voice & audio' :
                             index === 2 ? 'Image generation' :
                             'Supporting tool'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technical Specs */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-settings-3-line text-orange-400"></i>
                    Technical Specifications
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Resolution', value: project.resolution, icon: 'ri-aspect-ratio-line' },
                      { label: 'Frame Rate', value: `${project.fps} fps`, icon: 'ri-speed-line' },
                      { label: 'Aspect Ratio', value: project.aspectRatio, icon: 'ri-layout-masonry-line' },
                      { label: 'Quality', value: project.quality, icon: 'ri-hd-line' },
                    ].map((spec) => (
                      <div key={spec.label} className="text-center p-3 bg-slate-700/30 rounded-lg">
                        <i className={`${spec.icon} text-xl text-orange-400 mb-2`}></i>
                        <p className="text-xs text-slate-400 mb-1">{spec.label}</p>
                        <p className="text-sm font-semibold text-white">{spec.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <ProjectRatings project={project} reviews={reviews} />
            )}

            {activeTab === 'comments' && (
              <ProjectComments comments={comments} totalCount={project.comments} />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ProjectSidebar project={project} relatedProjects={relatedProjects} />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIProjectDetailPage;
